// AttendanceForm — teacher's daily roster + status grid.
//
// Loads the school's students on mount, defaults every status to
// `present`, and lets the teacher toggle through `present → absent →
// late` per student before bulk-submitting. Each record carries a
// fresh UUIDv4 client_uuid so the server can dedupe replays on flaky
// mobile networks — the polish-#7 SDK contract requires it.
//
// `excused` and `half_day` exist server-side and in the SDK union but
// are intentionally not in this v0 click-cycle. They're rarer and
// adding a fourth state to the touch grid hurts readability. A future
// polish round adds an overflow menu per row.

import { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Button,
  Card,
  EmptyState,
  Spinner,
  Stack,
  tokens,
} from "@sms/design-system";
import type { Locale } from "@sms/i18n";
import {
  type AttendanceStatus,
  type Client,
  isApiError,
  type Student,
} from "@sms/sdk";

import { makeT } from "../../i18n";

export interface AttendanceFormProps {
  locale?: Locale;
  client: Pick<Client, "listStudents" | "submitAttendance">;
  /** Override today's date for tests / time-travel. Defaults to new Date(). */
  now?: () => Date;
}

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; reason: string }
  | { kind: "ready"; students: Student[] };

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "success"; accepted: number; deduped: number }
  | { kind: "error"; message: string };

// The cycle UI offers — order matters: tapping cycles forward.
const CYCLE: AttendanceStatus[] = ["present", "absent", "late"];

function next(status: AttendanceStatus): AttendanceStatus {
  const i = CYCLE.indexOf(status);
  if (i < 0) return "present";
  return CYCLE[(i + 1) % CYCLE.length] ?? "present";
}

export function AttendanceForm({ locale = "ne", client, now }: AttendanceFormProps) {
  const t = makeT(locale);
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [statuses, setStatuses] = useState<Record<string, AttendanceStatus>>({});
  const [submit, setSubmit] = useState<SubmitState>({ kind: "idle" });

  const dateAd = useMemo(() => formatDate((now ?? (() => new Date()))()), [now]);

  useEffect(() => {
    let cancelled = false;
    client
      .listStudents()
      .then((students) => {
        if (cancelled) return;
        const initial: Record<string, AttendanceStatus> = {};
        for (const s of students) initial[s.id] = "present";
        setStatuses(initial);
        setLoad({ kind: "ready", students });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoad({
            kind: "error",
            reason: err instanceof Error ? err.message : String(err),
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  const cycleStatus = (studentId: string) => {
    setStatuses((prev) => ({
      ...prev,
      [studentId]: next(prev[studentId] ?? "present"),
    }));
  };

  const markAllPresent = () => {
    if (load.kind !== "ready") return;
    const all: Record<string, AttendanceStatus> = {};
    for (const s of load.students) all[s.id] = "present";
    setStatuses(all);
  };

  const onSubmit = async () => {
    if (load.kind !== "ready") return;
    setSubmit({ kind: "submitting" });
    try {
      const records = load.students.map((s) => ({
        student_id: s.id,
        status: statuses[s.id] ?? "present",
        client_uuid: uuidv4(),
      }));
      const res = await client.submitAttendance({
        date_ad: dateAd,
        records,
      });
      setSubmit({ kind: "success", accepted: res.accepted, deduped: res.deduped });
    } catch (err) {
      if (isApiError(err) && err.problem.detail) {
        setSubmit({ kind: "error", message: err.problem.detail });
      } else {
        setSubmit({ kind: "error", message: t("attendance.fail") });
      }
    }
  };

  return (
    <section aria-labelledby="attendance-heading" style={containerStyle}>
      <header style={pageHeaderStyle}>
        <p style={overlineStyle}>{t("attendance.date_today", { date: dateAd })}</p>
        <h1 id="attendance-heading" style={headingStyle}>
          {t("attendance.title")}
        </h1>
      </header>

      {load.kind === "loading" && (
        <Stack gap={3} align="center" style={{ padding: tokens.space[10] }}>
          <Spinner size={28} label={t("common.loading")} />
        </Stack>
      )}
      {load.kind === "error" && (
        <Alert intent="danger">{t("attendance.fetch_failed")}</Alert>
      )}
      {load.kind === "ready" && load.students.length === 0 && (
        <EmptyState
          icon={
            <img
              src="/empty/no-wards.svg"
              alt=""
              width={120}
              height={120}
              style={{ display: "block" }}
            />
          }
          title={t("attendance.no_students")}
        />
      )}

      {load.kind === "ready" && load.students.length > 0 && (
        <Stack gap={4}>
          <Stack direction="row" justify="space-between" align="center">
            <p style={countLabelStyle}>
              {t("attendance.students_count", {
                count: String(load.students.length),
              })}
            </p>
            <Button variant="secondary" onClick={markAllPresent}>
              {t("attendance.mark_all_present")}
            </Button>
          </Stack>

          <Card variant="flat" padding={2}>
            <ul style={listStyle}>
              {load.students.map((s, i) => (
                <StudentRow
                  key={s.id}
                  student={s}
                  status={statuses[s.id] ?? "present"}
                  onCycle={() => cycleStatus(s.id)}
                  t={t}
                  locale={locale}
                  isLast={i === load.students.length - 1}
                />
              ))}
            </ul>
          </Card>

          {submit.kind === "success" && (
            <Alert intent="success">
              <span data-accepted={submit.accepted}>
                {t("attendance.success_message", {
                  accepted: String(submit.accepted),
                  deduped: String(submit.deduped),
                })}
              </span>
            </Alert>
          )}
          {submit.kind === "error" && (
            <Alert intent="danger">{submit.message}</Alert>
          )}

          <Button
            type="button"
            onClick={() => void onSubmit()}
            disabled={submit.kind === "submitting"}
            size="lg"
            fullWidth
          >
            {submit.kind === "submitting"
              ? t("attendance.submitting")
              : t("attendance.submit")}
          </Button>
        </Stack>
      )}
    </section>
  );
}

function StudentRow({
  student,
  status,
  onCycle,
  t,
  locale,
  isLast,
}: {
  student: Student;
  status: AttendanceStatus;
  onCycle: () => void;
  t: (k: string) => string;
  locale: Locale;
  isLast: boolean;
}) {
  const displayName = pickName(student, locale);
  const statusLabel = t(`status.${statusKey(status)}`);
  return (
    <li
      style={rowStyle(isLast)}
      data-testid={`row-${student.id}`}
      data-status={status}
    >
      <span style={nameStyle}>{displayName}</span>
      <button
        type="button"
        onClick={onCycle}
        style={statusPillStyle(status)}
        aria-label={`status for ${displayName}: ${statusLabel}`}
      >
        {statusLabel}
      </button>
    </li>
  );
}

function pickName(s: Student, locale: Locale): string {
  if (locale === "ne") {
    const first = s.first_name_ne ?? s.first_name;
    const last = s.last_name_ne ?? s.last_name;
    return `${first} ${last}`.trim();
  }
  return `${s.first_name} ${s.last_name}`.trim();
}

function statusKey(status: AttendanceStatus): string {
  if (status === "half_day") return "halfDay";
  return status;
}

function formatDate(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "Asia/Kathmandu",
  }).format(d);
}

function uuidv4(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ── styles ───────────────────────────────────────────────────────────

const containerStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 640,
  margin: "0 auto",
  padding: tokens.space[4],
};
const pageHeaderStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: tokens.space[1],
  marginBottom: tokens.space[5],
};
const overlineStyle: React.CSSProperties = {
  fontSize: tokens.type.size.xs,
  fontWeight: tokens.type.weight.semibold,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: tokens.color.semantic.textMuted,
};
const headingStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: tokens.type.weight.bold,
  color: tokens.color.semantic.heading,
  letterSpacing: "-0.02em",
  lineHeight: 1.2,
  margin: 0,
};
const countLabelStyle: React.CSSProperties = {
  fontSize: tokens.type.size.sm,
  color: tokens.color.semantic.textMuted,
  margin: 0,
};
const listStyle: React.CSSProperties = {
  listStyle: "none",
  padding: 0,
  margin: 0,
};
const rowStyle = (isLast: boolean): React.CSSProperties => ({
  display: "flex",
  alignItems: "center",
  gap: tokens.space[3],
  padding: `${tokens.space[3]} ${tokens.space[3]}`,
  borderBottom: isLast
    ? "none"
    : `1px solid ${tokens.color.semantic.border}`,
});
const nameStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontWeight: tokens.type.weight.medium,
  color: tokens.color.semantic.heading,
  fontSize: tokens.type.size.base,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const statusPillStyle = (status: AttendanceStatus): React.CSSProperties => {
  // Each status has its own colour family from semantic tokens so the
  // pill reads as a status indicator at a glance, while still being a
  // tappable cycle button (48dp tap target floor).
  const palette: Record<AttendanceStatus, { bg: string; fg: string }> = {
    present: {
      bg: tokens.color.success[50],
      fg: tokens.color.success[700],
    },
    absent: {
      bg: tokens.color.danger[50],
      fg: tokens.color.danger[700],
    },
    late: {
      bg: tokens.color.warning[50],
      fg: tokens.color.warning[700],
    },
    excused: {
      bg: tokens.color.info[50],
      fg: tokens.color.info[700],
    },
    half_day: {
      bg: "#ede9fe",
      fg: "#5b21b6",
    },
  };
  const p = palette[status];
  return {
    minWidth: 104,
    minHeight: tokens.size.tap48,
    padding: `${tokens.space[2]} ${tokens.space[4]}`,
    background: p.bg,
    color: p.fg,
    border: 0,
    borderRadius: tokens.radius.pill,
    fontWeight: tokens.type.weight.semibold,
    fontSize: tokens.type.size.sm,
    cursor: "pointer",
    letterSpacing: "0.01em",
    transition: `background ${tokens.motion.durBase} ${tokens.motion.easeOut}`,
  };
};
